/*
 * Helmet lanyard reminder.
 *
 * Shows a non-blocking offer in the add-to-cart notification and on the cart page
 * whenever the cart holds a NeuroGuard+ mouthguard and no lanyard. Never interrupts
 * add-to-cart or checkout.
 *
 * Markup + trigger product list live in snippets/ng-lanyard-offer.liquid.
 */
(function () {
  if (window.__ngLanyardOffer) return;
  window.__ngLanyardOffer = true;

  var SESSION_DISMISSED_KEY = 'ngLanyardDismissed';
  var MODAL_ANSWERED_KEY = 'ngLanyardModalAnswered';
  // Guard counts at the moment of the last dismissal/answer. If the cart later
  // holds MORE mouthguards than that, the reminder re-arms - the shopper's "no"
  // covered the old cart, not the new gear.
  var DISMISSED_GUARDS_KEY = 'ngLanyardDismissedGuards';
  var ANSWERED_GUARDS_KEY = 'ngLanyardAnsweredGuards';

  /* ------------------------------------------------------------------ *
   * Color matching.
   *
   * The lanyard swatch pre-selects to the color of the mouthguard the shopper
   * just picked, so saying "yes" is likely to land the color they wanted. They
   * pick ONE color for all lanyards - deliberately not one per mouthguard,
   * which the team judged more confusing than it is worth.
   *
   * Mouthguards come in Red, Blue, Purple, Pink, Yellow, Green, Orange, Black,
   * Clear and Neon Green. Lanyards come in Gray, Yellow, Green, Blue, Red and
   * Purple. Five names match outright, Neon Green aliases to Green, and the
   * remaining four have no lanyard equivalent, so they fall back to Gray - the
   * neutral option, and the lanyard's own first swatch.
   *
   * Colors reach the cart two different ways. neuroguardplus and
   * the-neuroguard-team-ordering are real variants, so the color is a product
   * option. The DoublePack and the Double Protection bundle are single-variant
   * products whose colors are collected by the Aris product-options app, which
   * writes them to a line item property ("Choose Your Color Options") as
   * "Blue, Red" when two are picked. We read both, and take the first color of
   * a pair - one color covers every lanyard added.
   *
   * ng-water-bottle-ng (the single-mouthguard Starter Bundle) collects no color
   * at all, so it stays on Gray until someone adds an option set to it.
   * ------------------------------------------------------------------ */
  var LANYARD_COLOR_FALLBACK = 'Gray';
  var LANYARD_COLOR_ALIASES = { 'neon green': 'green' };

  // Longest names first so 'Neon Green' is not swallowed by 'Green'. Used to pull
  // a color out of free-form property text, which can carry a price suffix.
  var KNOWN_COLORS = [
    'Neon Green', 'Yellow', 'Purple', 'Orange', 'Green', 'Black', 'Clear',
    'Blue', 'Gray', 'Grey', 'Pink', 'Red',
  ];

  var STRIP_SELECTORS = {
    swatch: '[data-ng-lanyard-swatch]',
    label: '[data-ng-lanyard-swatch-label]',
    match: '[data-ng-lanyard-match]',
  };

  var MODAL_SELECTORS = {
    swatch: '[data-ng-lanyard-modal-swatch]',
    label: '[data-ng-lanyard-modal-swatch-label]',
    match: '[data-ng-lanyard-modal-match]',
  };

  // Quantities of each mouthguard line at our last look at the cart, and the color
  // we deduced from it. Both live at module scope so they survive the cart page
  // re-rendering the offer strip.
  var guardSnapshot = null;
  var lastPickedColor = null;

  // A color the shopper picked for themselves. It outranks the prediction, and
  // persists across re-renders so their choice is not quietly reset to Gray.
  var colorChosenByShopper = null;

  function session(key, value) {
    try {
      if (value === undefined) return window.sessionStorage.getItem(key) === '1';
      window.sessionStorage.setItem(key, value ? '1' : '0');
    } catch (e) {
      // Private browsing / storage disabled - fall back to showing the offer.
    }
    return false;
  }

  function sessionNum(key) {
    try {
      return Number(window.sessionStorage.getItem(key)) || 0;
    } catch (e) {
      return 0;
    }
  }

  function sessionSetNum(key, value) {
    try {
      window.sessionStorage.setItem(key, String(value));
    } catch (e) {
      // Ignore - worst case the reminder shows again.
    }
  }

  // Highest guard count the shopper has already said no to (either surface).
  function answeredGuardCount() {
    var dismissed = session(SESSION_DISMISSED_KEY) ? sessionNum(DISMISSED_GUARDS_KEY) : -1;
    var answered = session(MODAL_ANSWERED_KEY) ? sessionNum(ANSWERED_GUARDS_KEY) : -1;
    return Math.max(dismissed, answered);
  }

  // A cart emptied of mouthguards starts a fresh conversation - earlier answers
  // covered gear that is gone now. Without this, a "no" given on a big cart
  // would silence the reminder for every smaller cart built afterwards.
  function clearAnswersIfNoGuards(counts) {
    if (counts.guards > 0) return;
    session(SESSION_DISMISSED_KEY, false);
    session(MODAL_ANSWERED_KEY, false);
    sessionSetNum(DISMISSED_GUARDS_KEY, 0);
    sessionSetNum(ANSWERED_GUARDS_KEY, 0);
  }

  // Pull a color out of one line item property value. The options app emits several
  // shapes and its exact property key is decided by app config we cannot read from
  // the theme, so we parse defensively: a bare 'Blue', a pair 'Blue, Red' (we take
  // the first - one color covers every lanyard), an aggregated
  // 'Choose Your Color Options: Blue, Red', and a price suffix 'Blue (+$5.00)'.
  //
  // The final comparison is EXACT against a known color, never a substring, which
  // is what lets us scan properties we cannot identify by key without an unrelated
  // one like 'Team Name: Red Hawks' masquerading as a color.
  function colorFromValue(value) {
    var first = String(value == null ? '' : value).split(',')[0];
    var colon = first.lastIndexOf(':');
    if (colon !== -1) first = first.slice(colon + 1);
    first = first
      .replace(/\([^)]*\)/g, '')
      .replace(/[-+*]?\s*\$[\d.,]+/g, '')
      .trim()
      .toLowerCase();
    for (var i = 0; i < KNOWN_COLORS.length; i++) {
      if (KNOWN_COLORS[i].toLowerCase() === first) return KNOWN_COLORS[i];
    }
    return null;
  }

  function itemColor(item) {
    if (!item) return null;

    // Real variants (neuroguardplus, team ordering) carry the color as an option.
    var options = item.options_with_values || [];
    for (var i = 0; i < options.length; i++) {
      if (String(options[i].name || '').toLowerCase() === 'color') return options[i].value;
    }

    // Bundles have no color variant - the options app records their colors as a
    // line item property instead. A key naming a color is trusted outright; failing
    // that we accept any property that parses cleanly to a color, because the app
    // also aggregates its options into a bookkeeping property whose key names no
    // color at all (and may be underscore-prefixed to hide it at checkout).
    var properties = item.properties || {};
    var aggregated = null;
    for (var key in properties) {
      if (!Object.prototype.hasOwnProperty.call(properties, key)) continue;
      var found = colorFromValue(properties[key]);
      if (!found) continue;
      var name = key.toLowerCase();
      if (name.indexOf('color') !== -1 || name.indexOf('colour') !== -1) return found;
      if (aggregated === null) aggregated = found;
    }
    if (aggregated) return aggregated;

    // Single-option products report their color as the variant title. Bundles
    // report 'Default Title', and a '/' means multiple options, neither of which
    // is a color.
    var title = item.variant_title;
    if (title && title !== 'Default Title' && title.indexOf('/') === -1) return title;
    return null;
  }

  // Which mouthguard color did the shopper pick most recently? Cart lines carry no
  // timestamp, so we diff against our previous look: the mouthguard line whose
  // quantity GREW is the one just added. That beats reading array order, which gets
  // it wrong when re-adding a variant bumps a line that is already in the cart.
  //
  // On the first look there is nothing to diff, so we fall back to array order.
  // /cart.js returns line items NEWEST FIRST (verified against the live store), so
  // the newest colored mouthguard is the FIRST one we encounter, not the last.
  function trackPickedColor(cart, map) {
    var next = {};
    var grew = null;
    var newestColored = null;
    var anyGuard = false;

    ((cart && cart.items) || []).forEach(function (item) {
      if (!map[item.handle]) return;
      anyGuard = true;
      next[item.key] = item.quantity;
      if (!newestColored && itemColor(item)) newestColored = item;
      if (guardSnapshot && item.quantity > (guardSnapshot[item.key] || 0)) grew = item;
    });

    guardSnapshot = next;

    // No mouthguards left means nothing to predict from.
    if (!anyGuard) {
      lastPickedColor = null;
      return null;
    }

    if (grew && itemColor(grew)) lastPickedColor = itemColor(grew);
    else if (lastPickedColor === null && newestColored) lastPickedColor = itemColor(newestColored);

    return lastPickedColor;
  }

  function swatchForColor(container, selector, color) {
    if (!color) return null;
    var want = String(color).toLowerCase();
    want = LANYARD_COLOR_ALIASES[want] || want;
    var options = container.querySelectorAll(selector);
    for (var i = 0; i < options.length; i++) {
      if (String(options[i].dataset.variantTitle || '').toLowerCase() === want) return options[i];
    }
    return null;
  }

  // Point the swatches at the shopper's own pick if they made one, otherwise at the
  // color we predicted from the cart. Falls back to Gray when the mouthguard color
  // has no lanyard twin, so we never silently pre-select something merely close.
  function applyColorMatch(container, selectors, predicted) {
    var options = container.querySelectorAll(selectors.swatch);
    if (!options.length) return;

    var match = swatchForColor(container, selectors.swatch, colorChosenByShopper || predicted);
    var target =
      match || swatchForColor(container, selectors.swatch, LANYARD_COLOR_FALLBACK) || options[0];

    Array.prototype.forEach.call(options, function (option) {
      option.setAttribute('aria-checked', option === target ? 'true' : 'false');
    });

    var label = container.querySelector(selectors.label);
    if (label) label.textContent = target.dataset.variantTitle;

    // Explain the color only while it is still our guess - once the shopper has
    // picked one the note would be telling them what they just did.
    var note = container.querySelector(selectors.match);
    if (note) {
      note.textContent =
        !colorChosenByShopper && match && predicted
          ? 'Matched to your ' + predicted + ' mouthguard'
          : '';
    }
  }

  function clearMatchNotes() {
    document
      .querySelectorAll('[data-ng-lanyard-match], [data-ng-lanyard-modal-match]')
      .forEach(function (note) {
        note.textContent = '';
      });
  }

  function strips() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-ng-lanyard]'));
  }

  // The card being open is the real condition - checking it directly means any add-to-cart
  // path counts, including apps that bypass Dawn's product-form and its cartUpdate event.
  function notificationIsOpen() {
    var card = document.getElementById('cart-notification');
    return Boolean(card && card.classList.contains('active'));
  }

  function cartUrl(path) {
    return (window.routes && window.routes[path]) || path;
  }

  function fetchCart() {
    return fetch(cartUrl('cart_url') + '.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .catch(function () {
        return null;
      });
  }

  function selectedVariant(strip) {
    var checked = strip.querySelector('[data-ng-lanyard-swatch][aria-checked="true"]');
    return checked || strip.querySelector('[data-ng-lanyard-swatch]');
  }

  function syncSwatchLabel(strip) {
    var label = strip.querySelector('[data-ng-lanyard-swatch-label]');
    var swatch = selectedVariant(strip);
    if (label && swatch) label.textContent = swatch.dataset.variantTitle;
  }

  function stripQty(strip) {
    var input = strip.querySelector('[data-ng-lanyard-qty]');
    var qty = parseInt(input ? input.value : strip.dataset.ngLanyardQty, 10);
    if (isNaN(qty) || qty < 1) qty = 1;
    if (qty > 99) qty = 99;
    return qty;
  }

  function plural(count, singular, pluralWord) {
    return count === 1 ? singular : pluralWord;
  }

  function syncStripAddLabel(strip) {
    var button = strip.querySelector('[data-ng-lanyard-add]');
    var unit = Number(strip.dataset.ngLanyardUnitPrice) || 0;
    if (!button || !unit) return;
    var qty = stripQty(strip);
    button.textContent =
      qty === 1
        ? 'Add lanyard — ' + money(unit)
        : 'Add ' + qty + ' lanyards — ' + money(unit * qty);
  }

  // One lanyard per mouthguard: the strip pre-fills the full shortfall. Runs on
  // every cart change, so the default tracks the cart; the shopper can still
  // adjust it with the stepper before adding.
  function syncStripQuantity(strip, counts) {
    var qty = Math.max(counts.guards - counts.lanyards, 1);
    strip.dataset.ngLanyardQty = qty;
    strip.dataset.ngLanyardGuards = counts.guards;

    var input = strip.querySelector('[data-ng-lanyard-qty]');
    if (input) input.value = qty;

    // When they already own some lanyards, spell out the mismatch - that's the
    // whole reason the strip is showing again.
    var note = strip.querySelector('[data-ng-lanyard-note]');
    if (note) {
      note.textContent =
        counts.lanyards > 0
          ? 'In your cart: ' +
            counts.guards + ' ' + plural(counts.guards, 'mouthguard', 'mouthguards') +
            ', but only ' +
            counts.lanyards + ' ' + plural(counts.lanyards, 'lanyard', 'lanyards') + '.'
          : '';
    }

    syncStripAddLabel(strip);
  }

  function show(strip) {
    strip.classList.add('ng-lanyard--visible');
    syncSwatchLabel(strip);
  }

  function hide(strip) {
    strip.classList.remove('ng-lanyard--visible');
  }

  // allowNotification is true only when an add-to-cart just opened the notification card.
  // Without it a plain page load would consume the once-per-session offer unseen.
  function evaluate(allowNotification) {
    var all = strips();
    if (!all.length) return Promise.resolve();

    return fetchCart().then(function (cart) {
      if (!cart) return;

      clearAnswersIfNoGuards(cartCounts(cart, all[0]));
      var predicted = trackPickedColor(cart, parseGuardMap(all[0].dataset.ngLanyardGuardMap));

      all.forEach(function (strip) {
        var counts = cartCounts(cart, strip);
        var deficit = counts.guards - counts.lanyards;

        // Dismissal only sticks for the cart it was given on: adding MORE
        // mouthguards afterwards re-arms the reminder.
        var suppressed =
          session(SESSION_DISMISSED_KEY) && counts.guards <= sessionNum(DISMISSED_GUARDS_KEY);

        if (deficit <= 0 || suppressed) {
          hide(strip);
          return;
        }

        syncStripQuantity(strip, counts);
        applyColorMatch(strip, STRIP_SELECTORS, predicted);

        // Offered on every qualifying add - it disappears once every mouthguard
        // has a lanyard or the shopper dismisses it for this cart.
        if (strip.dataset.ngLanyardContext === 'notification') {
          if (!allowNotification) {
            hide(strip);
            return;
          }
          show(strip);
          return;
        }

        show(strip);
      });
    });
  }

  function sectionInnerHTML(html, selector) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var node = doc.querySelector(selector);
    return node ? node.innerHTML : null;
  }

  function replaceSection(elementId, html, selector) {
    var element = document.getElementById(elementId);
    if (!element || !html) return;
    var inner = sectionInnerHTML(html, selector);
    if (inner !== null) element.innerHTML = inner;
  }

  function refreshNotification(response) {
    var sections = response.sections || {};
    var item = (response.items || [])[0];
    if (item) {
      replaceSection(
        'cart-notification-product',
        sections['cart-notification-product'],
        '[id="cart-notification-product-' + item.key + '"]'
      );
    }
    replaceSection('cart-notification-button', sections['cart-notification-button'], '.shopify-section');
    replaceSection('cart-icon-bubble', sections['cart-icon-bubble'], '.shopify-section');
  }

  function refreshCartPage() {
    var items = document.getElementById('main-cart-items');
    var footer = document.getElementById('main-cart-footer');
    if (!items || !footer) {
      window.location.reload();
      return Promise.resolve();
    }

    var ids = [items.dataset.id, footer.dataset.id, 'cart-icon-bubble'].filter(Boolean);

    return fetch(cartUrl('cart_url') + '?sections=' + ids.join(','))
      .then(function (response) {
        return response.json();
      })
      .then(function (sections) {
        var itemsHTML = sectionInnerHTML(sections[items.dataset.id] || '', '.js-contents');
        var footerHTML = sectionInnerHTML(sections[footer.dataset.id] || '', '.js-contents');
        if (itemsHTML === null || footerHTML === null) {
          window.location.reload();
          return;
        }
        items.querySelector('.js-contents').innerHTML = itemsHTML;
        footer.querySelector('.js-contents').innerHTML = footerHTML;
        replaceSection('cart-icon-bubble', sections['cart-icon-bubble'], '.shopify-section');
      })
      .catch(function () {
        window.location.reload();
      });
  }

  function addLanyard(strip) {
    var swatch = selectedVariant(strip);
    var button = strip.querySelector('[data-ng-lanyard-add]');
    var status = strip.querySelector('[data-ng-lanyard-status]');
    if (!swatch || !button || button.disabled) return;

    var isNotification = strip.dataset.ngLanyardContext === 'notification';
    var qty = stripQty(strip);

    button.disabled = true;
    status.classList.remove('ng-lanyard__status--error');
    status.textContent = 'Adding…';

    var body = {
      items: [{ id: Number(swatch.dataset.variantId), quantity: qty }],
    };

    if (isNotification) {
      body.sections = 'cart-notification-product,cart-notification-button,cart-icon-bubble';
      body.sections_url = window.location.pathname;
    }

    fetch(cartUrl('cart_add_url'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (response) {
        if (response.status && response.status !== 200) {
          throw new Error(response.description || response.message);
        }

        status.textContent =
          qty === 1
            ? '✓ Lanyard added to your order.'
            : '✓ ' + qty + ' lanyards added to your order.';

        if (isNotification) {
          refreshNotification(response);
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ng-lanyard-offer', cartData: response });
        }

        // On the cart page cart-items re-renders itself off that event; only step in if it is absent.
        var refreshed =
          isNotification || document.querySelector('cart-items')
            ? Promise.resolve()
            : refreshCartPage();

        // Let the confirmation read before the offer hides itself.
        return refreshed.then(function () {
          setTimeout(function () {
            evaluate(false);
          }, 1200);
        });
      })
      .catch(function (error) {
        console.error('Lanyard add failed', error);
        status.classList.add('ng-lanyard__status--error');
        status.textContent = 'Sorry, that lanyard could not be added. Please try again.';
        button.disabled = false;
      });
  }

  /* ------------------------------------------------------------------ *
   * Lanyard assistant modal (cart page only - the markup renders there).
   *
   * Opens at most once per session when the cart holds mouthguards and no
   * lanyard. Quantity pre-fills to one lanyard per mouthguard using the
   * guards-per-unit map in snippets/ng-lanyard-modal.liquid, so doublepacks
   * count as 2. Every exit path marks the question answered; it never
   * re-opens, never blocks checkout.
   * ------------------------------------------------------------------ */

  var modalLastFocus = null;
  var modalLastAdded = null;
  var modalCloseTimer = null;
  var modalSeenGuards = null;

  function modalRoot() {
    return document.querySelector('[data-ng-lanyard-modal]');
  }

  // The theme scales <body> (transform: scale(0.9)) on screens wider than
  // 900px, and a transformed ancestor hijacks position: fixed - the modal
  // would render mid-page instead of as a viewport overlay. Hoisting it out
  // of <body> into <html> restores true fixed positioning.
  function hoistModal() {
    var modal = modalRoot();
    if (modal && modal.parentElement !== document.documentElement) {
      document.documentElement.appendChild(modal);
    }
  }

  function modalIsOpen() {
    var modal = modalRoot();
    return Boolean(modal && !modal.hidden);
  }

  function parseGuardMap(raw) {
    var map = {};
    (raw || '').split(',').forEach(function (pair) {
      var parts = pair.split(':');
      if (parts[0]) map[parts[0].trim()] = Number(parts[1]) || 0;
    });
    return map;
  }

  // guards = mouthguard units in the cart (line qty x guards-per-unit).
  function cartCounts(cart, modal) {
    var map = parseGuardMap(modal.dataset.ngLanyardGuardMap);
    var lanyardHandle = modal.dataset.ngLanyardHandle;
    var counts = { guards: 0, lanyards: 0 };
    ((cart && cart.items) || []).forEach(function (item) {
      if (item.handle === lanyardHandle) counts.lanyards += item.quantity;
      else if (map[item.handle]) counts.guards += map[item.handle] * item.quantity;
    });
    return counts;
  }

  // The store sells in USD only; cart.js prices arrive in cents.
  function money(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function modalQty(modal) {
    var input = modal.querySelector('[data-ng-lanyard-modal-qty]');
    var qty = parseInt(input && input.value, 10);
    if (isNaN(qty) || qty < 1) qty = 1;
    if (qty > 99) qty = 99;
    return qty;
  }

  function syncModalSummary(modal) {
    var qty = modalQty(modal);
    var unit = Number(modal.dataset.ngLanyardUnitPrice) || 0;
    var summary = modal.querySelector('[data-ng-lanyard-modal-summary]');
    var label = qty === 1 ? '1 lanyard' : qty + ' lanyards';
    if (summary) summary.textContent = label + ' (' + money(unit * qty) + ')';
    return label;
  }

  function modalSelectedSwatch(modal) {
    return (
      modal.querySelector('[data-ng-lanyard-modal-swatch][aria-checked="true"]') ||
      modal.querySelector('[data-ng-lanyard-modal-swatch]')
    );
  }

  function syncModalSwatchLabel(modal) {
    var label = modal.querySelector('[data-ng-lanyard-modal-swatch-label]');
    var swatch = modalSelectedSwatch(modal);
    if (label && swatch) label.textContent = swatch.dataset.variantTitle;
  }

  function modalOpen(modal, guards, predicted) {
    var input = modal.querySelector('[data-ng-lanyard-modal-qty]');
    if (input) input.value = guards;
    syncModalSummary(modal);
    applyColorMatch(modal, MODAL_SELECTORS, predicted);
    modal.hidden = false;
    document.body.classList.add('ng-lanyard-modal-open');
    modalLastFocus = document.activeElement;
    var dialog = modal.querySelector('.ng-lanyard-modal__dialog');
    if (dialog) dialog.focus();
  }

  // Every close counts as an answer for the CURRENT cart - the modal only
  // returns if more mouthguards are added afterwards.
  function modalClose(modal) {
    session(MODAL_ANSWERED_KEY, true);
    sessionSetNum(ANSWERED_GUARDS_KEY, Number(modal.dataset.ngLanyardGuards) || 0);
    if (modalCloseTimer) {
      clearTimeout(modalCloseTimer);
      modalCloseTimer = null;
    }
    modal.hidden = true;
    document.body.classList.remove('ng-lanyard-modal-open');
    if (modalLastFocus && modalLastFocus.focus) modalLastFocus.focus();
    modalLastFocus = null;
  }

  function evaluateModal(fromCartUpdate) {
    var modal = modalRoot();
    if (!modal || modalIsOpen()) return;

    fetchCart().then(function (cart) {
      if (!cart) return;
      var counts = cartCounts(cart, modal);
      clearAnswersIfNoGuards(counts);
      var predicted = trackPickedColor(cart, parseGuardMap(modal.dataset.ngLanyardGuardMap));

      var previousGuards = modalSeenGuards;
      modalSeenGuards = counts.guards;

      var deficit = counts.guards - counts.lanyards;
      if (deficit <= 0) return;
      // An earlier "no" (on either surface) holds until the cart gains more
      // mouthguards than it covered.
      if (counts.guards <= answeredGuardCount()) return;
      // Mid-page cart edits only re-open the modal when mouthguards were
      // ADDED. Removing a line (a product, or the lanyards themselves) must
      // never pop a dialog at the shopper.
      if (fromCartUpdate && previousGuards !== null && counts.guards <= previousGuards) return;

      hoistModal();
      modal.dataset.ngLanyardGuards = counts.guards;
      modalOpen(modal, deficit, predicted);
    });
  }

  function modalShowDone(modal, text) {
    var ask = modal.querySelector('[data-ng-lanyard-modal-ask]');
    var done = modal.querySelector('[data-ng-lanyard-modal-done]');
    var doneText = modal.querySelector('[data-ng-lanyard-modal-done-text]');
    if (ask) ask.hidden = true;
    if (done) done.hidden = false;
    if (doneText) doneText.textContent = text;
  }

  function modalAdd(modal) {
    var swatch = modalSelectedSwatch(modal);
    var button = modal.querySelector('[data-ng-lanyard-modal-add]');
    var status = modal.querySelector('[data-ng-lanyard-modal-status]');
    if (!swatch || !button || button.disabled) return;

    var qty = modalQty(modal);
    var label = syncModalSummary(modal);

    button.disabled = true;
    status.classList.remove('ng-lanyard__status--error');
    status.textContent = 'Adding…';

    fetch(cartUrl('cart_add_url'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        items: [{ id: Number(swatch.dataset.variantId), quantity: qty }],
      }),
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (response) {
        if (response.status && response.status !== 200) {
          throw new Error(response.description || response.message);
        }

        modalLastAdded = { id: Number(swatch.dataset.variantId), qty: qty };
        session(MODAL_ANSWERED_KEY, true);
        status.textContent = '';
        button.disabled = false;

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ng-lanyard-offer', cartData: response });
        }

        // cart-items re-renders itself off the cartUpdate event; step in only if absent.
        var refreshed = document.querySelector('cart-items') ? Promise.resolve() : refreshCartPage();

        return refreshed.then(function () {
          modalShowDone(modal, 'Added ' + label + ' to your cart.');
          evaluate(false);
          // Leave time to read and undo, then get out of the way.
          modalCloseTimer = setTimeout(function () {
            if (modalIsOpen()) modalClose(modal);
          }, 8000);
        });
      })
      .catch(function (error) {
        console.error('Lanyard add failed', error);
        status.classList.add('ng-lanyard__status--error');
        status.textContent = 'Sorry, the lanyards could not be added. Please try again.';
        button.disabled = false;
      });
  }

  function modalUndo(modal) {
    if (!modalLastAdded) {
      modalClose(modal);
      return;
    }

    var added = modalLastAdded;
    modalLastAdded = null;

    // The cart may have held lanyards before the modal topped it up, so undo
    // subtracts exactly what the modal added rather than zeroing the line.
    fetchCart()
      .then(function (cart) {
        var current = 0;
        ((cart && cart.items) || []).forEach(function (item) {
          if (item.variant_id === added.id) current = item.quantity;
        });
        var updates = {};
        updates[added.id] = Math.max(0, current - added.qty);
        return fetch(cartUrl('cart_update_url'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ updates: updates }),
        });
      })
      .then(function (response) {
        return response.json();
      })
      .then(function (response) {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ng-lanyard-offer', cartData: response });
        }
        var refreshed = document.querySelector('cart-items') ? Promise.resolve() : refreshCartPage();
        return refreshed.then(function () {
          evaluate(false);
          modalClose(modal);
        });
      })
      .catch(function () {
        // Leave the modal up; the shopper can still remove the line in the cart.
        modalClose(modal);
      });
  }

  document.addEventListener('click', function (event) {
    var modal = event.target.closest('[data-ng-lanyard-modal]');
    if (!modal) return;

    var swatch = event.target.closest('[data-ng-lanyard-modal-swatch]');
    if (swatch) {
      modal.querySelectorAll('[data-ng-lanyard-modal-swatch]').forEach(function (option) {
        option.setAttribute('aria-checked', option === swatch ? 'true' : 'false');
      });
      // Their pick now outranks the prediction, here and on the cart strip.
      colorChosenByShopper = swatch.dataset.variantTitle;
      clearMatchNotes();
      syncModalSwatchLabel(modal);
      return;
    }

    if (event.target.closest('[data-ng-lanyard-modal-minus]')) {
      var minusInput = modal.querySelector('[data-ng-lanyard-modal-qty]');
      minusInput.value = Math.max(1, modalQty(modal) - 1);
      syncModalSummary(modal);
      return;
    }

    if (event.target.closest('[data-ng-lanyard-modal-plus]')) {
      var plusInput = modal.querySelector('[data-ng-lanyard-modal-qty]');
      plusInput.value = Math.min(99, modalQty(modal) + 1);
      syncModalSummary(modal);
      return;
    }

    if (event.target.closest('[data-ng-lanyard-modal-undo]')) {
      modalUndo(modal);
      return;
    }

    if (event.target.closest('[data-ng-lanyard-modal-add]')) {
      modalAdd(modal);
      return;
    }

    if (
      event.target.closest('[data-ng-lanyard-modal-no]') ||
      event.target.closest('[data-ng-lanyard-modal-close]')
    ) {
      modalClose(modal);
    }
  });

  document.addEventListener('input', function (event) {
    if (!event.target.matches || !event.target.matches('[data-ng-lanyard-modal-qty]')) return;
    var modal = event.target.closest('[data-ng-lanyard-modal]');
    if (modal) syncModalSummary(modal);
  });

  // Normalize typed values (blank, 0, 250) once the field loses focus.
  document.addEventListener(
    'change',
    function (event) {
      if (!event.target.matches || !event.target.matches('[data-ng-lanyard-modal-qty]')) return;
      var modal = event.target.closest('[data-ng-lanyard-modal]');
      if (!modal) return;
      event.target.value = modalQty(modal);
      syncModalSummary(modal);
    },
    true
  );

  document.addEventListener('keydown', function (event) {
    var modal = modalRoot();
    if (!modal || modal.hidden) return;

    if (event.key === 'Escape') {
      modalClose(modal);
      return;
    }

    // Keep Tab inside the dialog while it is open.
    if (event.key !== 'Tab') return;
    var focusables = Array.prototype.filter.call(
      modal.querySelectorAll('button, input'),
      function (element) {
        return !element.disabled && element.offsetParent !== null;
      }
    );
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  // Delegated so the offer keeps working after the cart or notification re-renders.
  document.addEventListener('click', function (event) {
    if (!event.target.closest('[data-ng-lanyard]')) return;
    // The controls are type="submit" to dodge CartNotification's close binding; keep them inert.
    event.preventDefault();

    var swatch = event.target.closest('[data-ng-lanyard-swatch]');
    if (swatch) {
      var strip = swatch.closest('[data-ng-lanyard]');
      strip.querySelectorAll('[data-ng-lanyard-swatch]').forEach(function (option) {
        option.setAttribute('aria-checked', option === swatch ? 'true' : 'false');
      });
      // Their pick now outranks the prediction, on every surface.
      colorChosenByShopper = swatch.dataset.variantTitle;
      clearMatchNotes();
      syncSwatchLabel(strip);
      return;
    }

    var minus = event.target.closest('[data-ng-lanyard-minus]');
    var plus = event.target.closest('[data-ng-lanyard-plus]');
    if (minus || plus) {
      var stepStrip = (minus || plus).closest('[data-ng-lanyard]');
      var stepInput = stepStrip.querySelector('[data-ng-lanyard-qty]');
      if (stepInput) {
        stepInput.value = minus ? Math.max(1, stripQty(stepStrip) - 1) : Math.min(99, stripQty(stepStrip) + 1);
        syncStripAddLabel(stepStrip);
      }
      return;
    }

    var dismiss = event.target.closest('[data-ng-lanyard-dismiss]');
    if (dismiss) {
      var dismissedStrip = dismiss.closest('[data-ng-lanyard]');
      session(SESSION_DISMISSED_KEY, true);
      sessionSetNum(DISMISSED_GUARDS_KEY, Number(dismissedStrip.dataset.ngLanyardGuards) || 0);
      strips().forEach(hide);
      return;
    }

    var add = event.target.closest('[data-ng-lanyard-add]');
    if (add) addLanyard(add.closest('[data-ng-lanyard]'));
  });

  document.addEventListener('input', function (event) {
    if (!event.target.matches || !event.target.matches('[data-ng-lanyard-qty]')) return;
    var strip = event.target.closest('[data-ng-lanyard]');
    if (strip) syncStripAddLabel(strip);
  });

  // Normalize typed values (blank, 0, 250) once the field loses focus.
  document.addEventListener(
    'change',
    function (event) {
      if (!event.target.matches || !event.target.matches('[data-ng-lanyard-qty]')) return;
      var strip = event.target.closest('[data-ng-lanyard]');
      if (!strip) return;
      event.target.value = stripQty(strip);
      syncStripAddLabel(strip);
    },
    true
  );

  // Exposed for debugging: run ngLanyardOfferState() in the console to see why it is hidden.
  window.ngLanyardOfferState = function () {
    return fetchCart().then(function (cart) {
      var items = (cart && cart.items) || [];
      var source = strips()[0] || modalRoot();
      var counts = source ? cartCounts(cart, source) : null;
      var state = {
        stripsOnPage: strips().length,
        modalOnPage: Boolean(modalRoot()),
        modalOpen: modalIsOpen(),
        guardCount: counts ? counts.guards : 'n/a (no offer markup on page)',
        lanyardCount: counts ? counts.lanyards : 'n/a (no offer markup on page)',
        lanyardDeficit: counts ? counts.guards - counts.lanyards : 'n/a',
        predictedColor: lastPickedColor || '(none - falls back to ' + LANYARD_COLOR_FALLBACK + ')',
        colorChosenByShopper: colorChosenByShopper || '(none yet)',
        dismissedThisSession: session(SESSION_DISMISSED_KEY),
        modalAnsweredThisSession: session(MODAL_ANSWERED_KEY),
        guardsCoveredByEarlierNo: answeredGuardCount(),
        cartHandles: items.map(function (i) {
          return i.handle;
        }),
        guardLines: items
          .filter(function (i) {
            return source && parseGuardMap(source.dataset.ngLanyardGuardMap)[i.handle];
          })
          .map(function (i) {
            return {
              handle: i.handle,
              variant_title: i.variant_title,
              properties: i.properties,
              colorRead: itemColor(i) || '(none - this line yields no color)',
            };
          }),
        notificationOpen: notificationIsOpen(),
      };
      console.table(state);
      return state;
    });
  };

  function init() {
    hoistModal();
    evaluate(false);
    evaluateModal(false);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (data) {
        if (data && data.source === 'ng-lanyard-offer') return;
        evaluate(notificationIsOpen());
        // On the cart page, bumping a mouthguard's quantity can re-open the
        // modal - but only past the guard count an earlier answer covered.
        evaluateModal(true);
      });
    }

    // Catches every add-to-cart that opens the card, whoever performed it.
    var card = document.getElementById('cart-notification');
    if (card && window.MutationObserver) {
      new MutationObserver(function () {
        if (notificationIsOpen()) evaluate(true);
      }).observe(card, { attributes: true, attributeFilter: ['class'] });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
